import { Box, Button, Typography } from '@material-ui/core';

import { BulkPasteSummary } from '../../../components/BulkPasteSummary';
import { EmailField } from '../../../components/controlled-inputs';
import { PhoneNumberFieldV2 } from '../../../components/controlled-inputs/PhoneNumberFieldV2';
import { emailPattern } from '../../../components/controlled-inputs';
import useStyles from '../styles';
import { BulkPhoneEntryApi } from '../types';
import { ContactList } from './ContactList';

interface Props {
  isPhoneInputDisabled?: boolean;
  handleEmailFieldKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  handlePhoneFieldKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  addEmailToList?: () => void;
  addPhoneToList: () => void;
  deleteContact: (contact: string) => void;
  moreClickCallback?: () => void;
  emails?: string[];
  phones: string[];
  setIsPhoneInputDisabled?: (isPhoneInputDisabled: boolean) => void;
  phoneOnly?: boolean;
  /** Hide the added-contacts chips, e.g. when the host renders its own summary of them. */
  hideContactList?: boolean;
  /** Block the phone input and its Add button, e.g. until a company is picked */
  disabled?: boolean;
  /** Enables pasting a whole list of numbers into the phone field. Omit to keep single entry only. */
  bulkPaste?: BulkPhoneEntryApi;
  /** Wording for the contacts cap, shown when a paste runs past it */
  bulkLimitHint?: string;
}

export const PhoneOrEmail = ({
  isPhoneInputDisabled = true,
  handleEmailFieldKeyDown,
  handlePhoneFieldKeyDown,
  addEmailToList,
  addPhoneToList,
  deleteContact,
  moreClickCallback,
  emails,
  phones,
  setIsPhoneInputDisabled,
  phoneOnly = false,
  hideContactList = false,
  disabled = false,
  bulkPaste,
  bulkLimitHint,
}: Props) => {
  const classes = useStyles();
  const showEmailInput = !phoneOnly && !isPhoneInputDisabled;
  const showPhoneInput = phoneOnly || isPhoneInputDisabled;
  const showInputToggle = !phoneOnly && !emails?.length && !phones?.length;

  return (
    <Box marginBottom={2} marginTop={2} alignItems="center">
      {showEmailInput && (
        <Box display="flex" gridGap={16} marginBottom={2}>
          <EmailField name="email" rules={{ pattern: emailPattern }} onKeyDown={handleEmailFieldKeyDown} />

          <Button onClick={addEmailToList} className={classes.addButton} variant="outlined">
            Add
          </Button>
        </Box>
      )}

      {showPhoneInput && (
        <Box display="flex" width="100%" gridGap={16} marginBottom={2} marginTop={1}>
          <PhoneNumberFieldV2
            name="phone"
            onKeyDown={handlePhoneFieldKeyDown}
            disabled={disabled}
            onPasteList={bulkPaste?.onPasteList}
          />

          <Button
            onClick={addPhoneToList}
            className={classes.addButton}
            variant="outlined"
            disabled={disabled}
          >
            Add
          </Button>
        </Box>
      )}

      {/* Sits under the input so it reads as the outcome of the paste, before the chips */}
      {bulkPaste?.result && (
        <BulkPasteSummary
          result={bulkPaste.result}
          onDismiss={bulkPaste.onDismiss}
          limitHint={bulkLimitHint}
        />
      )}

      {showInputToggle && setIsPhoneInputDisabled && (
        <Box display="flex" alignItems="center" width="100%" justifyContent="center" gridGap={4}>
          <Typography variant="body2" color="textSecondary">
            or
          </Typography>
          <Typography
            variant="body2"
            style={{ cursor: 'pointer', fontWeight: 600 }}
            color="primary"
            onClick={() => setIsPhoneInputDisabled(!isPhoneInputDisabled)}
          >
            use {!isPhoneInputDisabled ? 'Phone Number' : 'Email Address'}
          </Typography>
        </Box>
      )}

      {!hideContactList && !!emails?.length && (
        <ContactList
          contacts={emails}
          limit={2}
          deleteContact={deleteContact}
          moreClickCallback={moreClickCallback || (() => {})}
        />
      )}

      {!hideContactList && !!phones?.length && (
        <ContactList
          contacts={phones}
          limit={3}
          deleteContact={deleteContact}
          moreClickCallback={moreClickCallback || (() => {})}
        />
      )}
    </Box>
  );
};
